import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export interface CustomScrollbarProps {
  children: React.ReactNode;
  /** Wrapper className — sizing (height/flex) belongs here, not on children. */
  className?: string;
  /** Fixed thumb size in px along the scroll axis (height for vertical,
   *  width for horizontal-only) — keeps the thumb a constant grabbable
   *  size instead of native's proportional shrink. In `direction="both"`,
   *  this is the vertical thumb's height; see `thumbWidth` for horizontal. */
  thumbHeight?: number;
  /** Horizontal thumb width in px, used only in `direction="both"` mode.
   *  Defaults to `thumbHeight`. Ignored otherwise. */
  thumbWidth?: number;
  /** Fires on every scroll, alongside the component's own thumb-tracking. */
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  /** Applied to the scrollable element, for styles with no Tailwind utility. */
  style?: React.CSSProperties;
  /** Scroll axis. `'vertical'` (default) fills parent height. `'horizontal'`
   *  sizes to content height and scrolls sideways. `'both'` scrolls both
   *  axes with an independent thumb per overflowing axis. */
  direction?: 'vertical' | 'horizontal' | 'both';
  /** Applied to the scrollable element itself, not the outer wrapper — for
   *  callers whose scroll region is a semantic/interactive element. */
  role?: string;
  'aria-label'?: string;
  /** Applied to the scrollable element itself, not the outer wrapper. */
  dataTestId?: string;
  /** Which edge the vertical thumb sits past. `'right'` (default) matches
   *  native convention; use `'left'` when the right side has variable-width
   *  content that a gutter would crowd. No effect in horizontal-only mode. */
  thumbPosition?: 'left' | 'right';
  /** Vertical thumb inset from its box edge, in px. `true` sits flush (0px)
   *  instead of overshooting 10px past the edge — for containers with no
   *  spare space around them. A number sets a specific inset instead (e.g.
   *  `6` sits the thumb 6px in from the edge). Default `false` (10px
   *  overshoot). No effect in horizontal-only mode. */
  thumbInset?: boolean | number;
  /** When this value changes (e.g. a page number, or a composite of
   *  page/limit/filters), scroll position resets to the top/left. Content
   *  changes alone never reset scroll (see the `children` effect below) —
   *  this is the opt-in escape hatch for callers whose new content is
   *  conceptually a different "page" and should start scrolled to the top. */
  resetScrollKey?: string | number;
  /** Stop leftover scroll at this container's edges on both axes instead of
   *  chaining it to the outer scroller. By default vertical scroll chains
   *  (the outer page takes over once this one hits its top/bottom), so a
   *  nested CustomScrollbar never traps the wheel. Set this on overlays —
   *  dropdowns, popups, modals — where reaching the end of the list must
   *  not scroll the page behind them. Default `false`. */
  containOverscroll?: boolean;
}

const DEFAULT_THUMB_HEIGHT = 200;

/**
 * Scrollable container with a custom-drawn, fixed-size scroll thumb instead
 * of the browser's native (proportionally-sized) one.
 *
 * @see docs/DEVELOPMENT_GUIDELINES.md — "Custom Scrollbar: Use `CustomScrollbar`,
 * Not Native/CSS Scrollbar Styling"
 */
export const CustomScrollbar = forwardRef<HTMLDivElement, CustomScrollbarProps>(
  (
    {
      children,
      className,
      thumbHeight = DEFAULT_THUMB_HEIGHT,
      thumbWidth = thumbHeight,
      onScroll,
      style,
      direction = 'vertical',
      role,
      'aria-label': ariaLabel,
      dataTestId,
      thumbPosition = 'right',
      thumbInset = false,
      resetScrollKey,
      containOverscroll = false,
    },
    ref
  ) => {
    const tracksVertical = direction !== 'horizontal';
    const tracksHorizontal = direction !== 'vertical';
    const isBoth = direction === 'both';
    const contentRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => contentRef.current as HTMLDivElement);
    const [thumbPosY, setThumbPosY] = useState(0);
    const [hasOverflowY, setHasOverflowY] = useState(false);
    const [thumbPosX, setThumbPosX] = useState(0);
    const [hasOverflowX, setHasOverflowX] = useState(false);
    // State-driven, not CSS group-hover: — that Tailwind v4 combination
    // wasn't being generated in this project, leaving the thumb invisible.
    const [isHovered, setIsHovered] = useState(false);
    // Identifies "this" instance's own elements (the outer wrapper, content
    // div, and both thumbs) regardless of DOM position, so hover detection
    // below works whether the pointer is over the content, a thumb (a
    // sibling, not a descendant, of the content div), or a dead strip inside
    // the wrapper that neither one covers — e.g. the gap between a
    // thumbInset thumb and content that's inset by the caller's own
    // className padding, which the wrapper's own box still spans even
    // though nothing renders there.
    const instanceId = useId();
    // Only the nested CustomScrollbar nearest the pointer counts as hovered —
    // mouseenter/mouseleave alone can't distinguish this from a parent
    // CustomScrollbar, since the pointer never leaves the parent's own box
    // while it's over a nested child. Matching on `instanceId` (rather than
    // `closest('.pzn-custom-scrollbar-content') === contentRef.current`) also
    // fixes hovering a thumb directly: a thumb isn't a DOM descendant of the
    // content div, so the old content-only selector never matched it, and
    // the thumb would fade out exactly when the pointer landed on it.
    const handlePointerMove = useCallback(
      (e: React.MouseEvent) => {
        const nearest = (e.target as HTMLElement).closest?.(
          '[data-pzn-scrollbar-instance]'
        );
        setIsHovered(
          nearest?.getAttribute('data-pzn-scrollbar-instance') === instanceId
        );
      },
      [instanceId]
    );
    // Also shown while scrolling — touch devices have no persistent hover
    // state, so hover-only would never reveal the thumb during a touch-scroll.
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Also shown while the thumb itself is being dragged — the pointer can
    // move faster than mousemove-driven hover tracking during a fast drag.
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef<{
      axis: 'y' | 'x';
      startClient: number;
      startScroll: number;
    } | null>(null);

    const update = useCallback(() => {
      const el = contentRef.current;
      if (!el) {
        return;
      }

      if (tracksVertical) {
        const maxScrollY = el.scrollHeight - el.clientHeight;
        setHasOverflowY(maxScrollY > 0);
        if (maxScrollY <= 0) {
          setThumbPosY(0);
        } else {
          const maxThumbPosY = Math.max(0, el.clientHeight - thumbHeight);
          const ratioY = el.scrollTop / maxScrollY;
          setThumbPosY(
            Math.min(maxThumbPosY, Math.max(0, ratioY * maxThumbPosY))
          );
        }
      }

      if (tracksHorizontal) {
        const horizontalThumbSize = isBoth ? thumbWidth : thumbHeight;
        const maxScrollX = el.scrollWidth - el.clientWidth;
        setHasOverflowX(maxScrollX > 0);
        if (maxScrollX <= 0) {
          setThumbPosX(0);
        } else {
          const maxThumbPosX = Math.max(
            0,
            el.clientWidth - horizontalThumbSize
          );
          const ratioX = el.scrollLeft / maxScrollX;
          setThumbPosX(
            Math.min(maxThumbPosX, Math.max(0, ratioX * maxThumbPosX))
          );
        }
      }
    }, [thumbHeight, thumbWidth, tracksVertical, tracksHorizontal, isBoth]);

    const handleScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        update();
        setIsScrolling(true);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 800);
        onScroll?.(e);
      },
      [update, onScroll]
    );

    useEffect(
      () => () => {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      },
      []
    );

    // Translates thumb drag distance into content scroll distance — the
    // inverse of the ratio `update()` uses to place the thumb from scroll
    // position. Metrics are read live off `el` on every move rather than
    // cached at drag-start, so a mid-drag content resize can't desync it.
    const handleThumbPointerDown = useCallback(
      (axis: 'y' | 'x') => (e: React.PointerEvent<HTMLDivElement>) => {
        const el = contentRef.current;
        if (!el) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        // jsdom (unit tests) has no real pointer-capture implementation and
        // throws on the call — real browsers all support it, so this is
        // purely a test-environment guard, not a feature-detection concern.
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // no-op
        }
        dragRef.current = {
          axis,
          startClient: axis === 'y' ? e.clientY : e.clientX,
          startScroll: axis === 'y' ? el.scrollTop : el.scrollLeft,
        };
        setIsDragging(true);
      },
      []
    );

    const handleThumbPointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        const el = contentRef.current;
        if (!drag || !el) {
          return;
        }
        if (drag.axis === 'y') {
          const maxScrollY = el.scrollHeight - el.clientHeight;
          const maxThumbPosY = Math.max(0, el.clientHeight - thumbHeight);
          if (maxThumbPosY <= 0) {
            return;
          }
          const scrollDelta =
            ((e.clientY - drag.startClient) * maxScrollY) / maxThumbPosY;
          el.scrollTop = Math.min(
            maxScrollY,
            Math.max(0, drag.startScroll + scrollDelta)
          );
        } else {
          const horizontalThumbSize = isBoth ? thumbWidth : thumbHeight;
          const maxScrollX = el.scrollWidth - el.clientWidth;
          const maxThumbPosX = Math.max(
            0,
            el.clientWidth - horizontalThumbSize
          );
          if (maxThumbPosX <= 0) {
            return;
          }
          const scrollDelta =
            ((e.clientX - drag.startClient) * maxScrollX) / maxThumbPosX;
          el.scrollLeft = Math.min(
            maxScrollX,
            Math.max(0, drag.startScroll + scrollDelta)
          );
        }
        // Paint immediately instead of waiting for the async native scroll
        // event, so the thumb tracks the pointer without a lag.
        update();
      },
      [update, thumbHeight, thumbWidth, isBoth]
    );

    const handleThumbPointerUp = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          // no-op — see handleThumbPointerDown
        }
        dragRef.current = null;
        setIsDragging(false);
      },
      []
    );

    // Recomputes on mount and when the container's own box resizes. Does
    // NOT catch content resizing while the container stays the same size
    // (see the `children` effect below).
    useLayoutEffect(() => {
      update();
      const el = contentRef.current;
      if (!el) {
        return;
      }
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(el);
      return () => resizeObserver.disconnect();
    }, [update]);

    // Recomputes when `children` changes without the container resizing
    // (e.g. swapped tab content) — ResizeObserver above wouldn't catch this.
    useLayoutEffect(() => {
      update();
    }, [children, update]);

    // Resets scroll position when resetScrollKey changes — deliberately
    // separate from the children effect above, which only recomputes
    // overflow/thumb position and must never reset position on its own (a
    // re-render with the same "page" of content, e.g. a row's status
    // toggling, should keep wherever the user scrolled to).
    const prevResetScrollKeyRef = useRef(resetScrollKey);
    useLayoutEffect(() => {
      if (
        resetScrollKey === undefined ||
        prevResetScrollKeyRef.current === resetScrollKey
      ) {
        return;
      }
      prevResetScrollKeyRef.current = resetScrollKey;
      const el = contentRef.current;
      if (!el) {
        return;
      }
      el.scrollTop = 0;
      el.scrollLeft = 0;
      update();
    }, [resetScrollKey, update]);

    useEffect(() => {
      const el = contentRef.current;
      if (!el) {
        return;
      }
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }, [update]);

    const isVisible = isHovered || isScrolling || isDragging;

    const thumbOffset =
      typeof thumbInset === 'number' ? thumbInset : thumbInset ? 0 : -10;

    // Sizes via flex-item stretching, not height:100% — 'both' needs the
    // same trick as 'vertical'; only overflow-x differs between them.
    const overflowClassName = tracksVertical
      ? `flex-1 min-h-0 overflow-y-auto ${tracksHorizontal ? 'overflow-x-auto' : 'overflow-x-hidden'}`
      : 'overflow-x-auto overflow-y-hidden';

    // Vertical stays `auto` so the outer scroller takes over once this one
    // hits its top/bottom (scroll chaining). Horizontal is always contained:
    // a sideways trackpad swipe at a wide table's edge would otherwise
    // trigger browser back/forward navigation.
    const overscrollClassName = containOverscroll
      ? 'overscroll-contain'
      : 'overscroll-y-auto overscroll-x-contain';

    return (
      <div
        className={`relative ${tracksVertical ? 'flex flex-col ' : ''}${className ?? ''}`}
        data-pzn-scrollbar-instance={instanceId}
        onMouseEnter={handlePointerMove}
        onMouseMove={handlePointerMove}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Raw rule, not the .no-scrollbar Tailwind utility — that utility
            wasn't being generated by this project's Tailwind v4 build. */}
        <style>{`.pzn-custom-scrollbar-content::-webkit-scrollbar { display: none; }`}</style>
        <div
          ref={contentRef}
          className={`pzn-custom-scrollbar-content ${overscrollClassName} ${overflowClassName}`}
          style={{ scrollbarWidth: 'none', ...style }}
          onScroll={handleScroll}
          data-pzn-scrollbar-instance={instanceId}
          {...(role !== undefined && { role })}
          {...(ariaLabel !== undefined && { 'aria-label': ariaLabel })}
          {...(dataTestId !== undefined && { 'data-testid': dataTestId })}
        >
          {children}
        </div>
        {/* Positioned past the content edge into the surrounding layout's
            existing padding, so it never clips fixed-size content. Wider
            than the visible 4px bar whenever it overshoots (default
            thumbInset=false, and always for horizontal, which has no inset
            option) — the overshoot gap has no element in it otherwise, so
            hovering through it on the way to the bar drops isHovered and
            the thumb flickers away right as the pointer reaches it. The
            inner div is the actual visible bar; the outer one is the full
            hoverable/draggable hit area. Inline styles for the same
            Tailwind-generation reason as above. */}
        {tracksVertical && hasOverflowY && (
          <div
            aria-hidden="true"
            data-testid="custom-scrollbar-thumb-y"
            data-pzn-scrollbar-instance={instanceId}
            onPointerDown={handleThumbPointerDown('y')}
            onPointerMove={handleThumbPointerMove}
            onPointerUp={handleThumbPointerUp}
            onPointerCancel={handleThumbPointerUp}
            style={{
              position: 'absolute',
              ...(thumbPosition === 'left'
                ? { left: thumbOffset }
                : { right: thumbOffset }),
              width: Math.max(4, -thumbOffset),
              top: thumbPosY,
              height: thumbHeight,
              // Above Table's sticky-header z-index (30) — otherwise a
              // sticky header paints over the thumb wherever they overlap.
              zIndex: 40,
              display: 'flex',
              justifyContent:
                thumbPosition === 'left' ? 'flex-start' : 'flex-end',
              touchAction: 'none',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          >
            <div
              style={{
                width: 4,
                height: '100%',
                borderRadius: 9999,
                transition: 'opacity 150ms, background-color 150ms',
                opacity: isVisible ? 1 : 0,
                backgroundColor: isVisible ? '#a3b9c4' : '#D1DCE1',
              }}
            />
          </div>
        )}
        {tracksHorizontal && hasOverflowX && (
          <div
            aria-hidden="true"
            data-testid="custom-scrollbar-thumb-x"
            data-pzn-scrollbar-instance={instanceId}
            onPointerDown={handleThumbPointerDown('x')}
            onPointerMove={handleThumbPointerMove}
            onPointerUp={handleThumbPointerUp}
            onPointerCancel={handleThumbPointerUp}
            style={{
              position: 'absolute',
              bottom: -10,
              height: 10,
              left: thumbPosX,
              width: isBoth ? thumbWidth : thumbHeight,
              // Above Table's sticky-header z-index (30) — otherwise a
              // sticky header paints over the thumb wherever they overlap.
              zIndex: 40,
              display: 'flex',
              alignItems: 'flex-end',
              touchAction: 'none',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          >
            <div
              style={{
                width: '100%',
                height: 4,
                borderRadius: 9999,
                transition: 'opacity 150ms, background-color 150ms',
                opacity: isVisible ? 1 : 0,
                backgroundColor: isVisible ? '#a3b9c4' : '#D1DCE1',
              }}
            />
          </div>
        )}
      </div>
    );
  }
);
